import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function Login() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showRequestAccess, setShowRequestAccess] = useState(false)
  const [requestEmail, setRequestEmail] = useState('')
  const [requestReason, setRequestReason] = useState('')
  const [requestError, setRequestError] = useState('')
  const [requestSuccess, setRequestSuccess] = useState(false)
  const [requestLoading, setRequestLoading] = useState(false)

  async function handleLogin(e) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        throw error
      }

      if (data?.session) {
        console.log('[LOGIN] Success, redirecting to dashboard...')
        navigate('/dashboard', { replace: true })
      }
    } catch (error) {
      console.error('[LOGIN] Error:', error)
      setError(error.message || 'Login failed. Please try again.')
      setLoading(false)
    }
  }

  async function handleRequestAccess(e) {
    e.preventDefault()
    setRequestError('')
    setRequestSuccess(false)

    if (requestReason.trim().length < 20) {
      setRequestError('Please provide at least 20 characters explaining why you need access.')
      return
    }

    setRequestLoading(true)

    try {
      const { error } = await supabase
        .from('access_requests')
        .insert({
          email: requestEmail,
          reason: requestReason
        })

      if (error) {
        if (error.code === '23505') {
          throw new Error('A request with this email already exists. Please check your inbox or contact support.')
        }
        throw error
      }

      setRequestSuccess(true)
      setRequestEmail('')
      setRequestReason('')
      setTimeout(() => {
        navigate('/thank-you')
      }, 1500)
    } catch (error) {
      console.error('[REQUEST ACCESS] Error:', error)
      setRequestError(error.message || 'Failed to submit request. Please try again.')
    } finally {
      setRequestLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div className="card" style={{ maxWidth: '450px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '36px', marginBottom: '14px', textAlign: 'center', fontWeight: '800', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.8)', fontFamily: 'VT323, monospace', letterSpacing: '2px' }}>
            &gt; AgencySniper
          </h1>
          <h2 style={{ fontSize: '28px', marginBottom: '14px', textAlign: 'center', fontWeight: '700', color: '#98FB98', textShadow: '0 0 12px rgba(152, 251, 152, 0.7)', fontFamily: 'VT323, monospace', letterSpacing: '2px' }}>
            &gt; SYSTEM ACCESS
          </h2>
          <p style={{ textAlign: 'center', color: 'rgba(144, 238, 144, 0.85)', marginBottom: '24px', fontSize: '16px', lineHeight: '1.6', textShadow: '0 0 5px rgba(144, 238, 144, 0.3)' }}>
            Authenticate to access AgencySniper operations
          </p>
        </div>

        <form onSubmit={handleLogin}>
          <label className="label">Email Address</label>
          <input
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
          />

          <label className="label">Password</label>
          <input
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="••••••••"
          />

          {error && <div className="error">{error}</div>}

          <button
            type="submit"
            className="btn"
            disabled={loading}
            style={{ width: '100%', marginTop: '24px' }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{ marginTop: '32px', paddingTop: '28px', borderTop: '1px solid rgba(152, 251, 152, 0.25)' }}>
          <p style={{ textAlign: 'center', fontSize: '15px', color: 'rgba(144, 238, 144, 0.8)', textShadow: '0 0 3px rgba(144, 238, 144, 0.3)', marginBottom: '16px' }}>
            Need access?{' '}
            <span
              onClick={() => setShowRequestAccess(!showRequestAccess)}
              style={{ color: '#90EE90', cursor: 'pointer', fontWeight: '700', textDecoration: 'underline', textDecorationColor: 'rgba(144, 238, 144, 0.4)', textShadow: '0 0 8px rgba(144, 238, 144, 0.6)' }}
            >
              Request Access
            </span>
          </p>

          {showRequestAccess && (
            <div style={{ marginTop: '20px', padding: '20px', background: 'rgba(34, 139, 34, 0.15)', borderRadius: '12px', border: '2px solid rgba(152, 251, 152, 0.3)' }}>
              <h3 style={{ fontSize: '20px', marginBottom: '16px', color: '#98FB98', textShadow: '0 0 10px rgba(152, 251, 152, 0.7)', fontFamily: 'VT323, monospace' }}>
                &gt; Request System Access
              </h3>
              <form onSubmit={handleRequestAccess}>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  className="input"
                  value={requestEmail}
                  onChange={(e) => setRequestEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  disabled={requestLoading}
                />

                <label className="label">Why do you need access?</label>
                <textarea
                  className="input"
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  required
                  placeholder="Please explain how you plan to use AgencySniper and why you need access to the system. Minimum 20 characters required."
                  style={{ minHeight: '100px', resize: 'vertical' }}
                  disabled={requestLoading}
                />
                <p style={{ fontSize: '12px', color: 'rgba(152, 251, 152, 0.65)', marginTop: '6px', textShadow: '0 0 3px rgba(152, 251, 152, 0.3)' }}>
                  {requestReason.length}/20 characters minimum
                </p>

                {requestError && <div className="error">{requestError}</div>}
                {requestSuccess && (
                  <div className="success">
                    Request submitted successfully! An administrator will review your request and contact you via email.
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowRequestAccess(false)
                      setRequestError('')
                      setRequestEmail('')
                      setRequestReason('')
                    }}
                    style={{ flex: 1 }}
                    disabled={requestLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    disabled={requestLoading || requestSuccess}
                    style={{ flex: 1 }}
                  >
                    {requestLoading ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: '20px' }}>
          <span
            onClick={() => navigate('/')}
            style={{ color: 'rgba(144, 238, 144, 0.7)', cursor: 'pointer', fontSize: '15px', textShadow: '0 0 3px rgba(144, 238, 144, 0.3)', fontWeight: '500' }}
          >
            ← Return to main
          </span>
        </p>
      </div>
    </div>
  )
}
